import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useTranslation } from 'react-i18next';
import { tokens as c, layout } from '../../theme';
import IconCircle from './IconCircle';

// Pieces for forms and settings pages, as the approved W2_Portal_Settings
// board and views/GeneralSettings.js draw them, with the owner's 28 Oct rule
// for quiet forms: one label per field, no helper lines on screen (a hint
// is read by screen readers and sits behind a small (i) for everyone else),
// errors under the field that is wrong, one primary button.

const srOnly = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** Visually hidden text (still read by screen readers). */
export function SrOnly({ id, children }) {
  return (
    <Box component="span" id={id} sx={srOnly}>
      {children}
    </Box>
  );
}

/** The orange dot beside a changed, unsaved field. */
export const UnsavedDot = ({ sx }) => (
  <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.ACCENT, flex: 'none', ...(sx || {}) }} />
);

/** The one label of a field, above it. htmlFor ties it to the input. */
export function FieldLabel({ htmlFor, id, children, changed, disabled, sx }) {
  return (
    <Typography
      component="label"
      htmlFor={htmlFor}
      id={id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 0.75,
        fontSize: 14.5,
        fontWeight: 600,
        lineHeight: 1.45,
        color: disabled ? c.TEXT_3 : c.INK,
        overflowWrap: 'anywhere',
        ...(sx || {}),
      }}
    >
      {changed ? <UnsavedDot /> : null}
      <span>{children}</span>
    </Typography>
  );
}

/** What is wrong with a field, under it: an icon and the words, in red. */
export function FieldError({ id, children, sx }) {
  if (!children) return null;
  return (
    <Box
      id={id}
      role="alert"
      sx={{
        mt: 0.75,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        color: c.DANGER_TEXT,
        fontSize: 13.5,
        fontWeight: 600,
        lineHeight: 1.45,
        overflowWrap: 'anywhere',
        ...(sx || {}),
      }}
    >
      <WarningAmberRoundedIcon aria-hidden="true" sx={{ fontSize: 18, flex: 'none', mt: '1px' }} />
      <span>{children}</span>
    </Box>
  );
}

/**
 * A small (i) that shows a hint on hover, focus or tap; the same words are
 * the field's accessible description (pass the id as describedBy).
 */
export function HintIcon({ id, hint }) {
  const [open, setOpen] = useState(false);
  if (!hint) return null;
  return (
    <>
      <SrOnly id={id}>{hint}</SrOnly>
      <Tooltip title={hint} open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)}>
        <IconButton
          size="small"
          aria-hidden="true"
          tabIndex={-1}
          onClick={() => setOpen((v) => !v)}
          sx={{ color: c.TEXT_3, p: 0.25, ml: -0.25 }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </>
  );
}

/**
 * One setting: its label (with an orange dot while unsaved and an (i) for
 * the hint) on the left, the control on the right; `layout="stack"` puts a
 * long control (a text area, a table) under the words, `layout="switch"`
 * keeps a switch on the same line on a phone too.
 *
 *   <SettingRow id="sms-sender" label={t('sms_sender')} hint={t('sms_sender_hint')}
 *     changed={draft.sender !== saved.sender} error={errors.sender}
 *     control={<TextField id="sms-sender" ... inputProps={{ 'aria-describedby': 'sms-sender-hint' }} />} />
 *
 * The control must use the same id (the label points at it); hints are read
 * through `${id}-hint`, errors through `${id}-error`.
 */
export function SettingRow({ id, label, hint, changed, error, control, layout: rowLayout, disabled, sx }) {
  const stacked = rowLayout === 'stack';
  const isSwitch = rowLayout === 'switch';
  return (
    <Box
      id={id ? `${id}-row` : undefined}
      sx={{ py: 1.75, borderBottom: `1px solid ${c.OUTLINE}`, '&:last-child': { borderBottom: 0 }, ...(sx || {}) }}
    >
      <Box sx={{ display: 'flex', flexWrap: isSwitch ? 'nowrap' : 'wrap', alignItems: 'flex-start', columnGap: 3, rowGap: 1.25 }}>
        <Box sx={{ flex: stacked ? '1 1 100%' : '1 1 260px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            component="label"
            htmlFor={id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: 15,
              fontWeight: 600,
              lineHeight: 1.45,
              color: disabled ? c.TEXT_3 : c.INK,
              cursor: isSwitch && !disabled ? 'pointer' : 'default',
              overflowWrap: 'anywhere',
            }}
          >
            {changed ? <UnsavedDot /> : null}
            <span>{label}</span>
          </Typography>
          <HintIcon id={id ? `${id}-hint` : undefined} hint={hint} />
        </Box>
        <Box
          sx={{
            flex: stacked ? '1 1 100%' : '0 1 auto',
            ml: isSwitch ? 'auto' : 0,
            minWidth: 0,
            maxWidth: '100%',
            width: stacked || isSwitch ? undefined : { xs: '100%', sm: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: stacked ? 'stretch' : { xs: isSwitch ? 'flex-end' : 'stretch', sm: 'flex-end' },
          }}
        >
          {control}
          {error ? <FieldError id={id ? `${id}-error` : undefined}>{error}</FieldError> : null}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * A titled card that groups settings or form fields (the panes of the
 * settings board): an icon on a tinted circle, the title, an optional
 * action on the right, then the rows.
 */
export function SectionCard({ icon, hue = 'accent', title, action, children, id, sx }) {
  return (
    <Paper component="section" aria-labelledby={id ? `${id}-title` : undefined} sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0, ...(sx || {}) }}>
      {title ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
          {icon ? <IconCircle icon={icon} hue={hue} size={34} /> : null}
          <Typography id={id ? `${id}-title` : undefined} component="h2" sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.4, color: c.INK, flex: 1, minWidth: 0 }}>
            {title}
          </Typography>
          {action || null}
        </Box>
      ) : null}
      {children}
    </Paper>
  );
}

/**
 * The save bar at the bottom of a settings card (sticky while it scrolls):
 * "2 unsaved changes", "1 to fix" (jumps to the first error), Discard and
 * Save changes. Place it as the last child of the card's Paper.
 *
 *   <SaveBar changedCount={n} errorCount={errs.length} onJumpToError={jump}
 *     onDiscard={discard} onSave={save} saving={saving} />
 */
export function SaveBar({ changedCount = 0, errorCount = 0, onJumpToError, onDiscard, onSave, saving = false, saveLabel, sx }) {
  const { t } = useTranslation();
  const hasChanges = changedCount > 0;
  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        mt: 'auto',
        bgcolor: c.WHITE,
        borderTop: `1px solid ${c.OUTLINE}`,
        borderBottomLeftRadius: `${layout.RADIUS_CARD}px`,
        borderBottomRightRadius: `${layout.RADIUS_CARD}px`,
        boxShadow: hasChanges ? '0 -6px 16px rgba(15,23,42,0.06)' : 'none',
        px: { xs: 2, md: 3 },
        pt: 1.5,
        pb: 'calc(12px + env(safe-area-inset-bottom))',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 1.5,
        rowGap: 1,
        ...(sx || {}),
      }}
    >
      <Box aria-live="polite" sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 0.5 }}>
        {hasChanges ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, fontWeight: 600, color: c.INK }}>
            <UnsavedDot />
            {changedCount === 1
              ? t('settings_unsaved_one', { defaultValue: '1 unsaved change' })
              : t('settings_unsaved_many', { n: changedCount, defaultValue: '{{n}} unsaved changes' })}
          </Box>
        ) : (
          <Box sx={{ fontSize: 14, color: c.TEXT_3 }}>{t('settings_all_saved', { defaultValue: 'No unsaved changes' })}</Box>
        )}
        {errorCount > 0 ? (
          <ButtonBase
            onClick={onJumpToError}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 14, fontWeight: 600, color: c.DANGER_TEXT, borderRadius: '6px', px: 0.5, mx: -0.5 }}
          >
            <WarningAmberRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
            {t('settings_to_fix', { n: errorCount, defaultValue: '{{n}} to fix' })}
          </ButtonBase>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
        {onDiscard ? (
          <Button variant="text" color="neutral" onClick={onDiscard} disabled={!hasChanges || saving}>
            {t('settings_discard', { defaultValue: 'Discard' })}
          </Button>
        ) : null}
        <Button variant="contained" startIcon={<CheckRoundedIcon />} onClick={onSave} disabled={!hasChanges || saving}>
          {saving ? t('settings_saving', { defaultValue: 'Saving…' }) : saveLabel || t('settings_save_changes', { defaultValue: 'Save changes' })}
        </Button>
      </Box>
    </Box>
  );
}
